import {
  Button,
  Input,
  InputAmount,
  InputDay,
  InputWrapper,
} from '@vrplatform/components';
import {
  showApiErrorNotification,
  useApiClient,
  useEnabledFeatures,
  useInvalidateApiQueries,
  useTeam,
  type RequestBody,
} from '@vrplatform/data';
import { showSuccessNotification } from '@vrplatform/theme';
import {
  bankersRoundToCents,
  day,
  ensure,
  formatCurrency,
} from '@vrplatform/utils';
import { Box, Group, Modal, SegmentedControl, Stack } from '@mantine/core';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { buildBankingInitRequestBody } from '../../bankingInit.utils';

type UpdateAccountData = RequestBody<'put:/accounts/{id}'>;

type FormInputs = {
  title: string;
  bankingCategory: NonNullable<
    NonNullable<UpdateAccountData['banking']>['category']
  >;
  bankingType: NonNullable<NonNullable<UpdateAccountData['banking']>['type']>;
  lastDigits: string;
  startDate: Date | null;
  balance: number | null;
};

const CATEGORIES = [
  {
    value: ensure<FormInputs['bankingCategory']>('trust'),
    label: 'Yes',
  },
  {
    value: ensure<FormInputs['bankingCategory']>('operating'),
    label: 'No',
  },
];

const TYPES = [
  {
    value: ensure<FormInputs['bankingType']>('deposit'),
    label: 'Bank Account',
  },
  {
    value: ensure<FormInputs['bankingType']>('creditCard'),
    label: 'Credit Card',
  },
];

export const AddCsvBankModal = ({
  closeModal: cl,
  opened,
}: {
  closeModal: () => void;
  opened: boolean;
}) => {
  const [{ statementStartAt }] = useTeam();
  const { OPEX } = useEnabledFeatures();

  const methods = useForm<FormInputs>({
    defaultValues: {
      bankingType: 'deposit',
      bankingCategory: 'trust',
      startDate: statementStartAt ? day(statementStartAt).toDate() : undefined,
    },
  });

  const goto = useNavigate();

  const closeModal = () => {
    cl();
    methods.reset({
      startDate: statementStartAt ? day(statementStartAt).toDate() : undefined,
      balance: null,
      bankingType: 'deposit',
      bankingCategory: 'trust',
      lastDigits: '',
      title: '',
    });
  };

  const invalidate = useInvalidateApiQueries(['bank-records', 'accounts']);

  const $api = useApiClient();

  const submit = async (data: FormInputs) => {
    try {
      const response = await $api.POST('/accounts', {
        body: {
          name: data.title,
          banking: {
            category: data.bankingCategory ?? 'trust',
            type: data.bankingType,
            last4: data.lastDigits,
          },
          type: 'bank',
        },
      });

      if (response.error) throw response.error;

      const bankAccount = response.data;

      let showSuccess = true;

      if (!OPEX ? data.bankingCategory === 'trust' : true) {
        await $api
          .POST('/accounts/{id}/banking-init', {
            params: {
              path: {
                id: bankAccount.id,
              },
            },
            body: buildBankingInitRequestBody({
              account: bankAccount,
              startingBalanceAmount: data.balance ?? 0,
              startingBalanceDate: day(data.startDate).yyyymmdd(),
            }),
          })
          .then((d) => {
            if (d.error) throw d.error;
          })
          .catch((error) => {
            showSuccess = false;
            showApiErrorNotification({
              error,
              title: 'Failed to update bank account',
              defaultMessage:
                'We failed to update the bank account. Please try again and contact support if the issue persists.',
            });
          });
      }

      if (showSuccess) {
        showSuccessNotification({
          title: 'Success!',
          message: 'Your bank accounts were successfully added.',
        });
      }

      invalidate();

      goto(`/reconciliation/account/${bankAccount.id}`);
    } catch (error) {
      showApiErrorNotification({
        error,
        title: 'Failed to update bank account',
        defaultMessage:
          'We failed to update the bank account. Please try again and contact support if the issue persists.',
      });
    }
  };

  // const isTrust =
  //   useWatch({
  //     control: methods.control,
  //     name: 'bankingCategory',
  //   }) === 'trust';

  const startDate =
    useWatch({
      control: methods.control,
      name: 'startDate',
    }) ?? day().toDate();

  return (
    <Modal
      centered
      radius="md"
      opened={opened}
      onClose={closeModal}
      title={'Configure your trust account'}
      size={550}
      styles={{
        title: {
          fontWeight: 500,
        },
      }}
    >
      <Box
        component="form"
        onReset={closeModal}
        onSubmit={methods.handleSubmit(submit)}
      >
        {/* Return all bank accounts that were added an allow to name each of them */}
        <Stack mb="xl">
          {/* Type */}
          <Controller
            control={methods.control}
            name={'bankingType'}
            rules={{
              required: 'Please select a type',
            }}
            render={({ field, fieldState }) => {
              return (
                <InputWrapper
                  label={'Account type'}
                  error={fieldState.error?.message}
                  sx={{
                    flexDirection: 'column',
                    display: 'flex',
                  }}
                >
                  <SegmentedControl
                    data={TYPES}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  />
                </InputWrapper>
              );
            }}
          />

          <Controller
            control={methods.control}
            name={'title'}
            defaultValue=""
            rules={{
              validate: (value) =>
                value.trim().length > 0 || 'Please enter a name',
            }}
            render={({ field, fieldState }) => (
              <InputWrapper
                label={'Account name'}
                error={fieldState.error?.message}
              >
                <Input
                  value={field.value}
                  onChange={field.onChange}
                  error={!!fieldState.error}
                  placeholder={'Account name'}
                />
              </InputWrapper>
            )}
          />

          <Controller
            control={methods.control}
            name={'lastDigits'}
            defaultValue=""
            rules={{
              validate: (value) => {
                if (!value?.trim().length) return 'Please enter last digits';

                if (value.length !== 4) return 'Please enter exactly 4 digits';

                return true;
              },
            }}
            render={({ field, fieldState }) => (
              <InputWrapper
                label={'Last 4 account digits'}
                error={fieldState.error?.message}
              >
                <Input
                  value={field.value}
                  onChange={field.onChange}
                  error={!!fieldState.error}
                  placeholder={'5221'}
                  maxLength={4}
                />
              </InputWrapper>
            )}
          />

          {/* Category */}
          {OPEX && (
            <Controller
              control={methods.control}
              name={'bankingCategory'}
              rules={{
                required: 'Please select if your account is a trust account',
              }}
              render={({ field, fieldState }) => {
                return (
                  <InputWrapper
                    label={'Is this a trust account?'}
                    error={fieldState.error?.message}
                    description="Define if this is a trust account or an operating account for your business"
                    sx={{
                      flexDirection: 'column',
                      display: 'flex',
                    }}
                    inputWrapperOrder={[
                      'label',
                      'input',
                      'description',
                      'error',
                    ]}
                  >
                    <SegmentedControl
                      data={CATEGORIES}
                      value={field.value}
                      onChange={(v) => {
                        field.onChange(v);
                      }}
                      onBlur={field.onBlur}
                    />
                  </InputWrapper>
                );
              }}
            />
          )}

          {/* Start Date */}
          <Controller
            control={methods.control}
            name={'startDate'}
            rules={{
              required: 'Please select a start date',
            }}
            render={({ field: { ref: _, ...field }, fieldState }) => {
              return (
                <InputWrapper
                  label={'Bank Feed Start Date'}
                  error={fieldState.error?.message}
                  sx={{
                    flexDirection: 'column',
                    display: 'flex',
                  }}
                >
                  <InputDay
                    {...field}
                    data-testid={field.name}
                    name={field.name}
                    placeholder={day(new Date()).format('LL')}
                    error={!!fieldState.error}
                    minDate={
                      statementStartAt
                        ? day(statementStartAt).toDate()
                        : undefined
                    }
                  />
                </InputWrapper>
              );
            }}
          />
          <Controller
            control={methods.control}
            name={'balance'}
            rules={{
              required: 'Please enter a balance',
            }}
            render={({ field: { ref: _, ...field }, fieldState }) => {
              return (
                <InputWrapper
                  label={`Balance as of ${day(startDate).subtract(1, 'day').format('ll')}`}
                  sx={{
                    flexDirection: 'column',
                    display: 'flex',
                  }}
                  error={fieldState.error?.message}
                >
                  <InputAmount
                    value={
                      typeof field.value === 'number'
                        ? field.value / 100
                        : (field.value ?? '')
                    }
                    setValue={(value) => {
                      if (typeof value === 'string') {
                        if (value === '') {
                          field.onChange('');
                          return;
                        }

                        const bankers = bankersRoundToCents(value);
                        if (bankers === field.value) return;
                      }
                      field.onChange(bankersRoundToCents(value));
                    }}
                    data-testid={field.name}
                    name={field.name}
                    error={!!fieldState.error}
                    decimalScale={2}
                    disabled={field.disabled}
                    placeholder={formatCurrency(0)}
                  />
                </InputWrapper>
              );
            }}
          />
        </Stack>

        <Group justify="flex-end">
          <Button type="reset" disabled={methods.formState.isSubmitting}>
            Cancel
          </Button>
          <Button
            key={'submit-button'}
            type="submit"
            variant="primary"
            loading={methods.formState.isSubmitting}
          >
            Submit
          </Button>
        </Group>
      </Box>
    </Modal>
  );
};
